import React from "react";
import { Helmet } from "react-helmet";

interface SEOProps {
  title: string;
  description?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  schemaData?: object;
  language?: "en" | "ar";
  alternates?: { language: string; href: string }[];
  children?: React.ReactNode;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = "website",
  twitterCard = "summary_large_image",
  schemaData,
  children,
  language = "en",
  alternates,
}) => (
  <Helmet>
    {language && <html lang={language} dir={language === "ar" ? "rtl" : "ltr"} />}
    {alternates?.map(item => <link key={item.language} rel="alternate" hrefLang={item.language} href={item.href} />)}
    <title>{title}</title>
    {description && <meta name="description" content={description} />}
    {canonical && <link rel="canonical" href={canonical} />}

    {/* Open Graph / Facebook */}
    <meta property="og:title" content={ogTitle || title} />
    {(ogDescription || description) && (
      <meta property="og:description" content={ogDescription || description} />
    )}
    {ogImage && <meta property="og:image" content={ogImage} />}
    {ogType && <meta property="og:type" content={ogType} />}
    {canonical && <meta property="og:url" content={canonical} />}

    {/* Twitter */}
    <meta name="twitter:title" content={ogTitle || title} />
    {(ogDescription || description) && <meta name="twitter:description" content={ogDescription || description} />}
    {canonical && <meta name="twitter:url" content={canonical} />}
    {twitterCard && <meta name="twitter:card" content={twitterCard} />}

    {/* Structured Data (JSON-LD) */}
    {schemaData && (
      <script type="application/ld+json">
        {JSON.stringify(schemaData).replace(/</g, "\\u003c")}
      </script>
    )}

    {children}
  </Helmet>
);

export default SEO;
